<template>
  <div class="w-full flex justify-around border-b border-gray-700 py-3 mb-3">
    <div class="w-1/3 text-sm text-gray-500 text-center">
      Check for missing and placeholder product images on the planner server
    </div>
  </div>
  <div class="w-full flex justify-center gap-2 py-4 pb-6">
    <button
      class="inline-flex items-center gap-2 h-12 cursor-pointer rounded-md bg-gray-800 px-4 py-3 text-center text-sm font-semibold uppercase text-white transition duration-200 ease-in-out hover:bg-gray-900"
      @click="startCheck"
      :disabled="running"
    >
      <ButtonSpinner v-if="running" />
      <span>{{ running ? "Checking..." : "Check Images" }}</span>
    </button>
  </div>
  <div class="px-4">
    <div v-if="hasRun && !running">
      <!-- Summary -->
      <div class="flex items-center gap-2 mb-4">
        <CheckIcon v-if="missingImages.length === 0 && placeholderImages.length === 0" class="h-6 text-green-600" />
        <ExclamationIcon v-else class="h-6 text-orange-600" />
        <span class="text-lg">
          <template v-if="missingImages.length === 0 && placeholderImages.length === 0">
            All images found!
          </template>
          <template v-else>
            {{ missingImages.length }} missing, {{ placeholderImages.length }} placeholders
          </template>
        </span>
        <span class="text-gray-500 text-sm">({{ checked }} checked)</span>
      </div>

      <!-- Two column layout -->
      <div style="display: flex">
        <!-- Missing images -->
        <div class="w-6/12 pr-4 border-r border-gray-700">
          <h3 class="text-red-400 font-semibold mb-2">
            Missing images: {{ missingImages.length }}
          </h3>
          <ul v-if="missingImages.length > 0" class="space-y-1">
            <li v-for="img in missingImages" :key="img.filename">
              <span class="text-red-400 font-mono text-sm">{{ img.filename }}</span>
              <span class="text-gray-500 text-sm"> - {{ img.collection }} {{ img.name }}</span>
            </li>
          </ul>
          <p v-else class="text-gray-500 text-sm">None</p>
        </div>

        <!-- Placeholder images -->
        <div class="w-6/12 pl-4">
          <h3 class="text-yellow-400 font-semibold mb-2">
            Placeholder images: {{ placeholderImages.length }}
          </h3>
          <ul v-if="placeholderImages.length > 0" class="space-y-1">
            <li v-for="img in placeholderImages" :key="img.filename">
              <span class="text-yellow-400 font-mono text-sm">{{ img.filename }}</span>
              <span class="text-gray-500 text-sm"> - {{ img.collection }} {{ img.name }}</span>
            </li>
          </ul>
          <p v-else class="text-gray-500 text-sm">None</p>
        </div>
      </div>
    </div>
    <div v-if="error" class="text-red-500">
      Error: {{ error }}
    </div>
  </div>
</template>

<script>
import axios from "axios";
import ButtonSpinner from "../Components/Icons/ButtonSpinner.vue";
import { CheckIcon, ExclamationIcon } from "@heroicons/vue/solid";

export default {
  components: {
    ButtonSpinner,
    CheckIcon,
    ExclamationIcon,
  },
  data() {
    return {
      running: false,
      hasRun: false,
      missingImages: [],
      placeholderImages: [],
      checked: 0,
      error: null,
      apiUrl: "https://cl-planner.com/check-images.php",
    };
  },
  methods: {
    async startCheck() {
      this.running = true;
      this.error = null;
      this.missingImages = [];
      this.placeholderImages = [];

      try {
        const products = this.prepareProductData();

        const response = await axios.post(this.apiUrl, {
          products: products,
        });

        if (response.data.success) {
          this.missingImages = response.data.missing;
          this.placeholderImages = response.data.placeholders || [];
          this.checked = response.data.checked;
        } else {
          throw new Error(response.data.error || "Unknown error");
        }
      } catch (err) {
        console.error("Image check failed:", err);
        this.error = err.message || "Failed to check images";
      } finally {
        this.running = false;
        this.hasRun = true;
      }
    },
    prepareProductData() {
      const productData = window.productData || [];
      const products = [];

      for (const product of productData) {
        // Skip hidden products (A == "K")
        if (product.A === "K") continue;

        // Skip products without O1 options
        if (!product.O1 || product.O1.length === 0) continue;

        const productEntry = {
          id: product.ID,
          name: product.NM_EN || "",
          collection: product.S || "",
          o1: product.O1,
        };

        // Include O2, B2, and T2 if they exist (for complex image patterns)
        // Complex pattern: ID_O1_B2_O2.jpg (when B2 exists and T2 doesn't contain "CUSH")
        if (product.O2 && product.O2.length > 0) {
          productEntry.o2 = product.O2;
          if (product.B2 && product.B2.length > 0) {
            productEntry.b2 = product.B2;
          }
        }

        // Include T2 to determine if it's a cushion type (affects filename pattern)
        if (product.T2) {
          productEntry.t2 = product.T2;
        }

        products.push(productEntry);
      }

      return products;
    },
  },
};
</script>

<style></style>
